/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { observer } from 'mobx-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button, IModuleProps, INodeProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import * as MsDyn365 from '@msdyn365-commerce/core';

import {
  submitContactFormAction,
  SubmitContactFormDataActionInput,
} from '../../data-actions/contact/submit-contact-commecial-form-data.action';

import { ICittaContactCommercialData } from './citta-contact-commercial.data';
import { ICittaContactCommercialProps } from './citta-contact-commercial.props.autogenerated';

interface ICittaContactFormState {
  name: string;
  emailAddress: string;
  occupation: string;
  phoneNumber: string;
  enquiry: string;
  checkbox:boolean;
  inputState: boolean;
  nameError: boolean;
  eAError: boolean;
  occupationError: boolean;
  pNError: boolean;
  enquiryError: boolean;
  checkboxError: boolean;
  reqErrorMsg: boolean;
  eAValErrorMsg: boolean;
  eAValidationError: boolean;
  responseReceived: boolean;
  rCCError: boolean;
  reCaptchaChecked: boolean;
  hideFirstElement: boolean;
  selectedValue: string;
  textareaheight:number;
  isCollapsed: boolean;
}

export interface ICittaContactCommercialViewProps extends ICittaContactCommercialProps<ICittaContactCommercialData> {
  ContactContainer: IModuleProps;
  ContactForm: INodeProps;
  EnquiryTitle:INodeProps
  Name: React.ReactNode;
  EmailAddress: React.ReactNode;
  Occupation: React.ReactNode;
  PhoneNumber: React.ReactNode;
  Enquiry: React.ReactNode;
  ErrorMessage: React.ReactNode;
  Recaptcha: React.ReactNode;
  SubmitButton: React.ReactNode;
  Checkbox: React.ReactNode;
  ExpandCollapse: React.ReactNode;
}

/**
 *
 * CittaContactCommercial component
 * @extends {React.PureComponent<ICittaContactCommercialProps<ICittaContactCommercialData>>}
 */
@observer
class CittaContactCommercial extends React.PureComponent<ICittaContactCommercialProps<ICittaContactCommercialData>, ICittaContactFormState> {

  public state: ICittaContactFormState = {
    name: '',
    emailAddress: '',
    occupation: '',
    phoneNumber: '',
    enquiry: '',
    checkbox:false,
    inputState: true,
    nameError: false,
    eAError: false,
    occupationError: false,
    pNError: false,
    enquiryError: false,
    checkboxError: false,
    reqErrorMsg: false,
    eAValErrorMsg: false,
    eAValidationError: false,
    responseReceived: false,
    rCCError: false,
    reCaptchaChecked: false,
    hideFirstElement: false,
    selectedValue: '',
    textareaheight: 1,
    isCollapsed: true
  };
  private _nameInputRef: React.RefObject<HTMLInputElement>;
  private _emailAddressInputRef: React.RefObject<HTMLInputElement>;
  private _occupationInputRef: React.RefObject<HTMLInputElement>;
  private _phoneNumberInputRef: React.RefObject<HTMLInputElement>;
  private _enquiryInputRef: React.RefObject<HTMLTextAreaElement>;
  private _checkboxRef: React.RefObject<HTMLInputElement>;
  private _recaptchaRef: React.RefObject<ReCAPTCHA>;

  private nameValue: boolean = false;
  private emailAddressValue: boolean = false;
  private occupationValue: boolean = false;
  private phoneNumberValue: boolean = false;
  private enquiryValue: boolean = false;
  private formInputValues: boolean = false;
  private requiredErrorMessage: string | undefined;
  private eAValidationErrorMessage: string | undefined;
  private recaptchaErrorMessage: string | undefined;
  private response: boolean = false;

  constructor(props: ICittaContactCommercialProps<ICittaContactCommercialData>) {
    super(props);
    this._nameInputRef = React.createRef();
    this._emailAddressInputRef = React.createRef();
    this._occupationInputRef = React.createRef();
    this._phoneNumberInputRef = React.createRef();
    this._enquiryInputRef = React.createRef();
    this._checkboxRef = React.createRef();
    this._recaptchaRef = React.createRef();
    this._handleSend = this._handleSend.bind(this);
    this._renderCollapseExpandButton = this._renderCollapseExpandButton.bind(this);
  }
  public render(): JSX.Element | null {

    const formProps = {
      className: `commercial-contact-form ${this.state.isCollapsed ? 'collapsed' : 'expanded'}`,
      onSubmit: this._handleSend,
      tag: 'form' as NodeTag
    };
    const viewProps = {
      ...this.props,
      ContactContainer: {
        moduleProps: this.props,
        className: 'commercial-citta-contact',
      },
      ContactForm: formProps,
      EnquiryTitle: this._renderEnquiryTitle(),
      Name: this._renderName(),
      ErrorMessage: this._renderErrorMessage(),
      EmailAddress: this._renderEmailAddress(),
      Occupation: this._renderOccupation(),
      PhoneNumber: this._renderPhoneNumber(),
      Enquiry: this._renderEnquiry(),
      Recaptcha: this._renderRecaptcha(),
      SubmitButton: this._renderSubmitButton(),
      Checkbox: this._renderCheckbox(),
      ExpandCollapse: this._renderCollapseExpandButton(),
    };

    return this.props.renderView(viewProps) as React.ReactElement;
  }

  private _renderCollapseExpandButton(): React.ReactNode {
    return (
      <div className="commercial-citta-contact__expand-container">
        {this.state.isCollapsed 
          ? <div className="expand-button" onClick={()=> {
            this.setState({isCollapsed: !this.state.isCollapsed})}
          }/>
          : <div className="collapse-button" onClick={()=> {
            this.setState({isCollapsed: !this.state.isCollapsed})}}/> 
        }
      </div>
    );
  }

  private _renderErrorMessage(): React.ReactNode {
    return (
      <div>
        {this.state.responseReceived && this.response === true ? (
          <div className='response-message'>
            <p className='response-message-content'>
              {this.props.resources.responseSuccessMessage}
            </p>
          </div>
        ) : null
        }
        {
          this.state.responseReceived && this.response === false ? (
            <div className='response-message'>
              <p className='response-error-message-content'>
                {this.props.resources.responseErrorMessage}
              </p>
            </div>
          ) : null
        }
      </div>
    );
  }


  private _renderEnquiryTitle():React.ReactNode {
    return(
      <div className="enquiryContent" id='contact-form'>
        <div className="enquiryTitle" id='contact-form-mobile'>
          {this.props.config.contactFormTitle || "have an enquiry?"}
        </div>
        <div className="enquiryText">
          {this.props.config.contactFormContent || "Interested in learning more about how Città Commercial can service your projects? Get in touch below and we will get back to you promptly"}
        </div>

        

      </div>
    )
  }
  private _renderName(): React.ReactNode {
    return (
      <div className='contact-form-name'>
        {this.state.nameError ? this._renderAlert('contact-form-name') : ''}
        <label id='contact-form-name__label' aria-label='name' />
        <input
          ref={this._nameInputRef}
          type='text'
          onChange={this.handleChange('nameInput')}
          className='contact-form-name-text'
          aria-label={this.props.resources.contactNamePlaceholderText}
          value={this.state.name}
          placeholder={this.props.resources.contactNamePlaceholderText}
          aria-labelledby='contact-form-name__label contact-form-name__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderOccupation(): React.ReactNode {
    return (
      <div className='contact-form-occupation'>
        {this.state.occupationError ? this._renderAlert('contact-form-occupation') : ''}
        <label id='contact-form-occupation__label' aria-label='occupation' />
        <input
          ref={this._occupationInputRef}
          type='text'
          onChange={this.handleChange('occupationInput')}
          className='contact-form-occupation-text'
          aria-label={this.props.resources.contactOccupationPlaceholderText}
          value={this.state.occupation}
          placeholder={this.props.resources.contactOccupationPlaceholderText}
          aria-labelledby='contact-form-occupation__label contact-form-occupation__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderEmailAddress(): React.ReactNode {
    return (
      <div className='contact-form-emailaddress'>
        {this.state.eAError || this.state.eAValidationError
          ? this._renderAlert('contact-form-emailaddress')
          : ''}
        <label
          id='contact-form-emailaddress__label'
          aria-label='emailaddress'
        />
        <input
          ref={this._emailAddressInputRef}
          type='text'
          onChange={this.handleChange('emailAddress')}
          className='contact-form-emailaddress-text'
          aria-label={this.props.resources.contactEmailAddressPlaceholderText}
          value={this.state.emailAddress}
          placeholder={
            this.props.resources.contactEmailAddressPlaceholderText
          }
          aria-labelledby='contact-form-emailaddress__label contact-form-emailaddress__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderPhoneNumber(): React.ReactNode {
    return (
      <div className='contact-form-phonenumber'>
        {this.state.pNError
          ? this._renderAlert('contact-form-phonenumber')
          : ''}
        <label
          id='contact-form-phonenumber__label'
          aria-label='phonenumber'
        />
        <input
          ref={this._phoneNumberInputRef}
          type='tel'
          onChange={this.handleChange('phoneNumber')}
          className='contact-form-phonenumber-text'
          aria-label={this.props.resources.contactPhoneNumberPlaceholderText}
          value={this.state.phoneNumber}
          placeholder={this.props.resources.contactPhoneNumberPlaceholderText}
          aria-labelledby='contact-form-phonenumber__label contact-form-phonenumber__error'
          aria-required='true'
        />
      </div>
    );
  }

  private _renderEnquiry(): React.ReactNode {
    return (
      <div className='contact-form-enquiry'>
        {this.state.enquiryError
          ? this._renderAlert('contact-form-enquiry')
          : ''}
        <label id='contact-form-enquiry__label' aria-label='enquiry' />
        <textarea
          ref={this._enquiryInputRef}
          onChange={this.onChangeTextArea}
          className='contact-form-enquiry-text'
          aria-label={this.props.resources.contactEnquiryPlaceholderText}
          value={this.state.enquiry}
          placeholder={this.props.resources.contactEnquiryPlaceholderText}
          aria-labelledby='contact-form-enquiry__label contact-form-enquiry__error'
          aria-required='true'
          rows={this.state.textareaheight}
        />
      </div>
    );
  }

 

  private _renderRecaptcha(): React.ReactNode {
    return (
      <div>
        {
          this.props.config.reCaptchaOption ? <div className='recaptcha'>
            {this.state.rCCError ? this._renderRecaptchaAlert('recaptcha-error') : ''}
            <ReCAPTCHA
              ref={this._recaptchaRef}
              sitekey={this.props.config.reCaptchaSiteKey!}
              onChange={this.onRecaptchaChange}
            />
          </div> : null
        }
      </div>
    );
  }

  private _renderSubmitButton(): React.ReactNode {
    return (
      <div className='contact-form-send'>
        <Button
          className='contact-form-send-button'
          title={this.props.resources.contactSubmitButtonText}
          onClick={this._handleSend}
          aria-label={this.props.resources.contactSubmitButtonText}
        >
          {this.props.resources.contactSubmitButtonText}
        </Button>
      </div>
    );
  }

  private _renderCheckbox(): React.ReactNode {
    return (
      <div className="contact-form-checkbox">
        <input
          ref={this._checkboxRef}
          type='checkbox'
          className='checkbox-text'
          aria-label={this.props.resources.contactCheckboxtext}
          aria-checked='true'
          onChange={this.handleChange('checkbox')}
          id='checkbox'
          checked={this.state.checkbox}
        />
        <span className='checkbox-content'>
          <p>{this.props.resources.contactCheckboxtext}</p>
        </span>
      </div>

    )
  }

  private onRecaptchaChange = (value: string | null) => {
    if (value) {
      this.setState({ reCaptchaChecked: true, rCCError: false });
    } else {
      this.setState({ reCaptchaChecked: false });
    }
  };

  private handleChange = (inputText: string) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    switch (inputText) {
      case 'nameInput': {
        this.setState({ name: value, nameError: false, reqErrorMsg: false });
        break;
      }
      case 'emailAddress': {
        this.setState({
          emailAddress: value,
          eAError: false,
          reqErrorMsg: false,
          eAValErrorMsg: false,
          eAValidationError: false,
        });
        break;
      }
      case 'occupationInput': {
        this.setState({
          occupation: value,
          eAError: false,
          reqErrorMsg: false,
          eAValErrorMsg: false,
          eAValidationError: false,
        });
        break;
      }
      case 'phoneNumber': {
        this.setState({
          phoneNumber: value,
          pNError: false,
          reqErrorMsg: false,
        });
        break;
      }
      case 'checkbox': {
        const isChecked = this._checkboxRef.current && this._checkboxRef.current.checked ? this._checkboxRef.current.checked: false;
        // console.log(this._checkboxRef.current, this._checkboxRef.current &&this._checkboxRef.current.checked )
        this.setState({ checkbox: isChecked, reqErrorMsg: false, eAValErrorMsg: false, checkboxError: false });
        break;
      }
      default:
        null;
    }
  };
  private onChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const height = e.target.scrollHeight;
    const rows = e.target.rows;
    const rowHeight = 19; 
    const trows = Math.ceil(height / rowHeight) - 2; 
    if (trows > rows) { 
      this.setState({
        textareaheight: trows
      })  
    } 
    this.setState({
      enquiry: e.target.value || '',
      reqErrorMsg: false,
      enquiryError: false,
    });
  };

  private isEmailValid = (email: string): boolean => {
    const regex = /^(([^<>()\[\]\\.,;:\s@']+(\.[^<>()\[\]\\.,;:\s@']+)*)|('.+'))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
  };

  private _renderAlert(className: string): JSX.Element {
    const inputBoxErrors =
      this.state.nameError ||
      this.state.eAError ||
      this.state.pNError ||
      this.state.enquiryError;
    if (inputBoxErrors) {
      this.requiredErrorMessage = this.props.resources.requiredTextErrorMessage;
    }
    if (this.state.emailAddress !== '' && this.state.eAValidationError) {
      this.eAValidationErrorMessage = this.props.resources.eAValidationErrorMessage;
    }
    const reqError = this.state.reqErrorMsg;
    const eAValError = this.state.eAValErrorMsg;
    return (
      <>
        {reqError && (
          <span
            className={`${className}__alert`}
            role='alert'
            aria-live='assertive'
          >
            {this.requiredErrorMessage && (
              <>
                <span
                  className={`${className}__alert-icon`}
                  aria-hidden='true'
                />
                {this.requiredErrorMessage}
              </>
            )}
          </span>
        )}
        {eAValError && (
          <span
            className={`${className}__alert`}
            role='alert'
            aria-live='assertive'
          >
            {this.eAValidationErrorMessage && (
              <>
                <span
                  className={`${className}__alert-icon`}
                  aria-hidden='true'
                />
                {this.eAValidationErrorMessage}
              </>
            )}
          </span>
        )}
      </>
    );
  }

  private _renderRecaptchaAlert(className: string): JSX.Element {
    if (this.props.config.reCaptchaOption) {
      if (!this.state.reCaptchaChecked) {
        this.recaptchaErrorMessage = this.props.resources.recaptchaErrorMessage;
      }
    }

    const rCCError = this.state.rCCError;
    return (
      <>
        {rCCError && (
          <span
            className={`${className}__alert`}
            role='alert'
            aria-live='assertive'
          >
            {this.recaptchaErrorMessage && (
              <>
                <span
                  className={`${className}__alert-icon`}
                  aria-hidden='true'
                />
                {this.recaptchaErrorMessage}
              </>
            )}
          </span>
        )}
      </>
    );
  }

  // tslint:disable-next-line: max-func-body-length cyclomatic-complexity
  private async _handleSend(
    e:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    this.nameValue =
      this._nameInputRef &&
        this._nameInputRef.current &&
        this._nameInputRef.current.value
        ? true
        : false;
    this.emailAddressValue =
      this._emailAddressInputRef &&
        this._emailAddressInputRef.current &&
        this._emailAddressInputRef.current.value
        ? true
        : false;
    this.occupationValue =
      this._occupationInputRef &&
        this._occupationInputRef.current &&
        this._occupationInputRef.current.value
        ? true
        : false;

    this.phoneNumberValue =
      this._phoneNumberInputRef &&
        this._phoneNumberInputRef.current &&
        this._phoneNumberInputRef.current.value
        ? true
        : false;

    this.enquiryValue =
      this._enquiryInputRef &&
        this._enquiryInputRef.current &&
        this._enquiryInputRef.current.value
        ? true
        : false;
    const eAVal = this._emailAddressInputRef.current!.value;

    const nA =
      this._nameInputRef &&
        this._nameInputRef.current &&
        this._nameInputRef.current.value
        ? this._nameInputRef.current.value
        : '';
    const eA =
      this._emailAddressInputRef &&
        this._emailAddressInputRef.current &&
        this._emailAddressInputRef.current.value
        ? this._emailAddressInputRef.current.value
        : '';
    const oV =
      this._occupationInputRef &&
        this._occupationInputRef.current &&
        this._occupationInputRef.current.value
        ? this._occupationInputRef.current.value
        : '';

    const pN =
      this._phoneNumberInputRef &&
        this._phoneNumberInputRef.current &&
        this._phoneNumberInputRef.current.value
        ? this._phoneNumberInputRef.current.value
        : '';

    const eV =
      this._enquiryInputRef &&
        this._enquiryInputRef.current &&
        this._enquiryInputRef.current.value
        ? this._enquiryInputRef.current.value
        : '';

    const cb = this._checkboxRef&& 
                 this._checkboxRef.current &&
                 this._checkboxRef.current?.checked
                 ? this._checkboxRef.current?.checked
                 : false;

    if (!this.nameValue) {
      this.setState({ nameError: true, reqErrorMsg: true });
    } else if (!this.emailAddressValue) {
      this.setState({ eAError: true, reqErrorMsg: true });
    } else if (!this.occupationValue) {
      this.setState({ occupationError: true, reqErrorMsg: true })
    } else if (!this.isEmailValid(eAVal)) {
      this.setState({ eAValidationError: true, eAValErrorMsg: true });
    } else if (!this.phoneNumberValue) {
      this.setState({ pNError: true, reqErrorMsg: true });
    } else if (!this.enquiryValue) {
      this.setState({ enquiryError: true, reqErrorMsg: true });
    } else if (
      this.props.config.reCaptchaOption &&
      !this.state.reCaptchaChecked
    ) {
      this.setState({ rCCError: true });
    } else {
      this.formInputValues =
        this.nameValue &&
          this.emailAddressValue &&
          this.occupationValue &&
          this.phoneNumberValue &&
          this.enquiryValue
          ? true
          : false;
      if (this.formInputValues) {
        try {

          //This Call is sending to AX
          const result = await submitContactFormAction(
            new SubmitContactFormDataActionInput(nA, eA, oV, pN, eV, cb),
            this.props.context.actionContext
          );
          this.response = result.success;


          // Send the form data to Emarsys
          const responseEmarsys = await MsDyn365.commerceApiRequest(
            this.props.context.request,
            'PostCommercialContact',
            'post',
            {
                name: nA,
                emailAddress: eA,
                occupation: oV,
                phoneNumber: pN,
                messageEnquiry: eV,
                checkbox:cb,
                emarsysOptInGroupId: this.props.config.emarsysOptInGroupId,
                optIn: true //or false... set this properly
            }  
          ) as any;

          if (this.response === true && responseEmarsys?.data.Result === 'Success') {
            // if (this.response === true) {
            this.setState({
              name: '',
              emailAddress: '',
              phoneNumber: '',
              enquiry: '',
              occupation:'',
              checkbox:false,
              responseReceived: true,
              reqErrorMsg: false,
              eAValErrorMsg: false,
              rCCError: false,
              reCaptchaChecked: false,
            });
            this._recaptchaRef.current?.reset();
          } else {
            console.log('wrong')
            this.setState({ responseReceived: true });
          }
        } catch {
          console.log('Please fill the form!');
        }
      }
    }
  }
}

export default CittaContactCommercial;
